import type {
  CSSProperties,
  AriaRole,
  HTMLAttributes,
  KeyboardEventHandler,
  MouseEvent,
  PointerEventHandler,
  ReactNode,
  Ref,
} from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import type { CcontainerProps } from "../Ccontainer";
import type {
  ButtonPreset,
  ContainerPreset,
} from "@/constants/componentPresets";
import type { ColorType } from "@/theme/colors";
import type { RadiusSize } from "@/theme/radius";
import type { FontWeight } from "@/theme/typography";

export const BUTTON_ICON_POSITION = {
  Left: "left",
  Right: "right",
} as const;

export type ButtonIconPosition =
  (typeof BUTTON_ICON_POSITION)[keyof typeof BUTTON_ICON_POSITION];

/**
 * Where an `href` button navigates. Vendor destinations leave the app, so the
 * new tab is the default; `Self` exists for the in-app case, which is the only
 * one where losing the current page is the intent.
 */
export enum CbuttonHrefTarget {
  Blank = "_blank",
  Self = "_self",
}

/**
 * Everything Cbutton writes onto the semantic root itself. Each of these has a
 * dedicated `CbuttonProps` field, and every one of them is resolved in
 * `resolveButtonRootAttributes` — so a caller spelling one into `rootAttributes`
 * would be handing Cbutton a value it is about to write over. Listing them here
 * turns that from a silent loss into a compile error that names the prop to use.
 *
 * Adding an attribute to the root means adding it to BOTH this list and the
 * resolver. That pairing is the whole contract.
 */
type ControlledButtonRootAttribute =
  | "children"
  | "className"
  | "style"
  | "role"
  | "tabIndex"
  | "title"
  | "onClick"
  | "onClickCapture"
  | "onKeyDown"
  | "onMouseEnter"
  | "onMouseLeave"
  | "onMouseDown"
  | "onMouseUp"
  | "onPointerDown"
  | "onPointerEnter"
  | "onPointerLeave"
  | "onPointerDownCapture"
  | "onTouchStart"
  | "onTouchEnd"
  | "onTouchCancel"
  | "aria-label"
  | "aria-pressed"
  | "aria-disabled";

type CbuttonDataAttributes = {
  [attribute in `data-${string}`]?: string | number | boolean | undefined;
};

/**
 * Element-agnostic on purpose: the same attribute bag is spread onto the div
 * root, the inline span root and the `href` anchor root, and an
 * `HTMLDivElement`-typed handler cannot be assigned to an anchor's. Every
 * handler Cbutton itself owns is excluded above, so what remains is the
 * passive attribute surface — which is identical on all three elements.
 */
export type CbuttonRootAttributes = Omit<
  HTMLAttributes<HTMLElement>,
  ControlledButtonRootAttribute
> &
  CbuttonDataAttributes;

export interface CbuttonProps {
  label?: string;
  /** Accessible name, applied as aria-label — required for icon-only buttons. */
  ariaLabel?: string;
  icon?: TablerIcon;
  iconSize?: number;
  iconOpacity?: number;
  iconStyle?: CSSProperties;
  /** Overrides the icon color; defaults to the preset's foreground (text) color. */
  iconColor?: ColorType;
  iconPosition?: ButtonIconPosition;
  textSize?: number;
  textWeight?: FontWeight;
  textColor?: ColorType;
  children?: ReactNode;
  beforeContent?: ReactNode;
  /** Ambient layer rendered behind the content and clipped to the radius (e.g. live sparks). */
  glowLayer?: ReactNode;
  preset?: ButtonPreset;
  containerPreset?: ContainerPreset;
  radius?: RadiusSize | number;
  padding?: string | number;
  height?: number | string;
  gap?: number;
  borderColor?: ColorType;
  borderOpacity?: number;
  backgroundOpacity?: number;
  backdropBlur?: number;
  onClick?: (event?: MouseEvent<HTMLDivElement>) => void;
  /**
   * Turns the semantic root into a real `<a>`. A `role="button"` div gives no
   * middle-click, no open-in-new-tab, no copy-link-address and no status-bar
   * preview, so any control whose job is "go to this URL" states that in the
   * markup instead of simulating it. Every preset, size, radius, motion and
   * state path is unchanged; only the root element and its semantics move.
   */
  href?: string;
  /** Only consulted when `href` is set. Defaults to a new tab. */
  hrefTarget?: CbuttonHrefTarget;
  /** The anchor root's ref, mirroring `rootRef` for the `href` branch. */
  anchorRootRef?: Ref<HTMLAnchorElement>;
  rootClassName?: string;
  title?: string;
  ariaPressed?: boolean;
  ariaDisabled?: boolean;
  /** Overrides the default button role for composite controls such as table rows. */
  rootRole?: AriaRole;
  /** Explicit focus participation for composite controls that own keyboard activation. */
  rootTabIndex?: number;
  /** Additional non-interaction attributes for the semantic root. */
  rootAttributes?: CbuttonRootAttributes;
  rootRef?: Ref<HTMLDivElement>;
  /** Renders a span-based root and surface for controls embedded in prose. */
  inline?: boolean;
  inlineRootRef?: Ref<HTMLSpanElement>;
  /** Keeps interaction ownership in Cbutton without adding a nested button role. */
  composite?: boolean;
  /** Pointer-down side effects such as stopping a parent drag gesture. */
  onPointerDown?: PointerEventHandler<HTMLDivElement>;
  onPointerEnter?: PointerEventHandler<HTMLDivElement>;
  onPointerLeave?: PointerEventHandler<HTMLDivElement>;
  /** Adds control-specific keys before Cbutton's Enter/Space activation. */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  disabled?: boolean;
  disableHoverEffects?: boolean;
  /**
   * Disables the hover-lift and press motion (the y-offset / scale) while keeping
   * hover state and visuals intact — e.g. when a custom hover background replaces the lift.
   */
  disableClickMotion?: boolean;
  /** Shows loading animation on the button container */
  isLoading?: boolean;
  containerStyle?: CSSProperties;
  /**
   * Style for the content layer inside the surface. On the non-inline roots
   * that is the `Ccontainer`'s content wrapper; the inline root has no separate
   * wrapper, so it lands on the inline surface itself — last, so it wins there
   * exactly as `contentWrapperStyle` wins on the wrapper.
   */
  contentStyle?: CSSProperties;
  /**
   * How the surface lays its content out on the two axes. Both reach the
   * `Ccontainer` as PROPS, which is why a caller's `style` cannot override
   * them — a `textAlign: start` on a centred surface loses silently. They
   * default to the centring every icon-and-label control wants; a control whose
   * content has to start on the leading edge (a title button in a scannable
   * list) sets `justifyContent` and gets a real left edge.
   */
  alignItems?: CcontainerProps["alignItems"];
  justifyContent?: CcontainerProps["justifyContent"];
  /**
   * Draws the surface border as an inset shadow instead of a CSS border, so it
   * costs no layout box. It is the one `Ccontainer` visual the caller cannot
   * reach through `containerStyle` — overriding `boxShadow` there would take the
   * glow and gloss layers down with it — so it is a prop, defaulted to the
   * literal Cbutton has always passed.
   */
  innerBorder?: boolean;
  stateContainerStyle?: {
    hover?: CSSProperties;
    pressed?: CSSProperties;
  };
  /** Motion transform values to preserve across hover/tap animations (e.g. { rotate: 135 }) */
  preserveStyle?: Record<string, number | string>;
  className?: string;
  style?: CSSProperties;
}
